import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronRight, ChevronDown, Construction, FileText, Download,
  FileDown, Sparkles, ListChecks, ClipboardList, FolderOpen, Calculator, GitBranch, Mail,
  Archive as ArchiveIcon, ArchiveRestore, Trash2, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import StatusPill from "../components/StatusPill";
import OutcomeChip from "../components/OutcomeChip";
import { Card, PrimaryButton, QuietButton, EmptyState } from "../components/UI";
import TenderEstimate from "./TenderEstimate";
import TenderPrelims from "./TenderPrelims";
import TenderProvsums from "./TenderProvsums";
import TenderSummary from "./TenderSummary";
import TenderOverview from "./TenderOverview";
import TenderAdjudication from "./TenderAdjudication";
import TenderAO from "./TenderAO";
import { PaymentScheduleTab } from "./tender/PaymentScheduleTab";
import { TenderDetailsTab } from "./tender/TenderDetailsTab";
import { VersionsTab } from "./tender/VersionsTab";
import { ClientEstimateTab } from "./tender/ClientEstimateTab";
import { OrderListTab } from "./tender/OrderListTab";
import { SubbyQuotesTab } from "./tender/SubbyQuotesTab";
import { EmailTab } from "./tender/EmailTab";
import { DrawingsTab } from "./tender/DrawingsTab";
import LifecycleStateBanner from "../components/LifecycleStateBanner";
import CreateClientEstimateDialog from "../components/CreateClientEstimateDialog";
import { AuditDrawer, HistoryButton } from "../components/AuditDrawer";
import ExportPdfDialog from "../components/ExportPdfDialog";
import SupplierEnquiryDrawer from "../components/SupplierEnquiryDrawer";
import { useAuth } from "../contexts/AuthContext";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { useEnabledBrands } from "../hooks/useEnabledBrands";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const BRAND_LABEL = {
  lux_construction: "Lux Construction",
  lux_developments: "Lux Developments"
};

function TabBar({ active, onChange }) {
  const [openSetup, setOpenSetup]   = useState(false);
  const [openOutput, setOpenOutput] = useState(false);

  const tab = (key, label) => (
    <button key={key} onClick={() => onChange(key)}
      className={["px-1 py-3 transition-colors relative",
        active === key ? "text-ink" : "text-ink-3 hover:text-ink-2"].join(" ")}
      style={{ fontSize: 13, fontWeight: active === key ? 600 : 500 }}>
      {label}
      {active === key && <span className="absolute left-0 right-0 -bottom-px h-[2px]" style={{ background: "#2F4F3E" }} />}
    </button>
  );

  const drop = (label, items, openState, setOpen) => (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="px-1 py-3 text-ink-3 hover:text-ink-2 inline-flex items-center gap-1"
        style={{ fontSize: 13, fontWeight: 500 }}>
        {label} <ChevronDown size={12} />
      </button>
      {openState && (
        <div className="absolute left-0 top-full mt-0.5 w-56 bg-surface border border-line rounded-md shadow-card z-30 overflow-hidden">
          {items.map((it) => (
            <button key={it.key}
              onMouseDown={() => { onChange(it.key); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
              style={{ fontSize: 12.5 }}>
              {it.icon ? <it.icon size={12} className="text-ink-3" /> : null}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex items-center gap-7 border-b border-line">
      {tab("summary",      "Summary")}
      {tab("estimate",     "Estimate")}
      {tab("prelims",      "Prelims")}
      {tab("adjudication", "Adjudication")}
      {tab("provsums",     "Pro Sums")}
      {tab("ao",           "A\u0026O")}
      {tab("notes",        "Notes")}
      {tab("subby",        "Subby Quotes")}
      {tab("drawings",     "Drawings")}
      <span className="h-5 w-px bg-line" />
      {drop("Output", [
        { key: "client_estimate",  label: "Client Estimate",   icon: FileText },
        { key: "payment_schedule", label: "Payment Schedule",  icon: Calculator },
        { key: "order_list",       label: "Order list",        icon: ListChecks },
        { key: "email",            label: "Send email",        icon: Mail },
        { key: "versions",         label: "Versions",          icon: GitBranch },
        { key: "documents",        label: "Documents",         icon: FolderOpen }
      ], openOutput, setOpenOutput)}
      {drop("Setup", [
        { key: "tender_details", label: "Tender Details",       icon: FileText },
        { key: "questionnaire",  label: "Questionnaire",        icon: ClipboardList }
      ], openSetup, setOpenSetup)}
    </div>
  );
}

function StatChip({ label, value, mono = false }) {
  return (
    <div className="flex flex-col">
      <span className="col-head">{label}</span>
      <span className={mono ? "font-mono tnum text-ink" : "text-ink"}
            style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{value}</span>
    </div>
  );
}

function TabContent({ tab, tender, onTenderUpdate }) {
  if (tab === "summary")
    // LT-Estimate-UX-01 Section 7 — Summary tab is now the 9-card
    // view-only overview. The old "Things to Note / What's Excluded"
    // component (TenderSummary) is preserved and reachable via the
    // "Notes" tab.
    return (
      <TenderOverview tender={tender} />
    );
  if (tab === "notes")
    return (
      <TenderSummary tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "estimate")
    return (
      <TenderEstimate tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "prelims")
    return (
      <TenderPrelims tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "provsums")
    return (
      <div className="mt-6"><TenderProvsums tenderId={tender.id} onTenderUpdate={onTenderUpdate} /></div>
    );
  if (tab === "adjudication")
    return (
      <div className="mt-6"><TenderAdjudication tenderId={tender.id} /></div>
    );
  if (tab === "ao" || tab === "assumptions")
    return (
      <TenderAO tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "payment_schedule")
    return (
      <PaymentScheduleTab tender={tender} />
    );
  if (tab === "tender_details")
    return (
      <TenderDetailsTab tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "versions")
    return (
      <VersionsTab tender={tender} />
    );
  if (tab === "client_estimate")
    // LT-17 §4 — real client-estimate viewer. Sent PDF (from GridFS)
    // when issued, live-preview iframe with "PREVIEW — NOT ISSUED"
    // watermark otherwise, status line always specific.
    return (
      <ClientEstimateTab tender={tender} />
    );
  if (tab === "order_list")
    // LT-20 — internal / merchant order list (buying quantities).
    // LT-37 — reached from the Output drop-down; key unchanged.
    return (
      <OrderListTab tender={tender} onTenderUpdate={onTenderUpdate} />
    );
  if (tab === "subby")
    // LT-37 — subby quotes (two locked PDF slots, revisions, void).
    return (
      <SubbyQuotesTab tender={tender} />
    );
  if (tab === "email")
    // LT-38 — request-for-quote email via mailto (Output drop-down).
    return (
      <EmailTab tender={tender} />
    );
  if (tab === "drawings")
    // LT-39 — tender drawings: upload, tile / list, in-app PDF viewer.
    return (
      <DrawingsTab tender={tender} />
    );
  const phaseInfo = {
    // LT-17 §4 — phaseInfo entries only remain for tabs whose target
    // component genuinely doesn't exist yet. Every time a component
    // ships, the entry gets deleted with the same PR so a stub never
    // outlives its feature.
    //
    // Stubs retired in this pass (auditing three known-stale entries
    // Chris hit):
    //   • `client_estimate`      — LT-13 shipped and LT-16 tightened
    //                              the flow; now rendered by
    //                              ClientEstimateTab above.
    //   • `payment_schedule`     — real tab at line 151, routed ahead
    //                              of the stub; entry deleted (dead
    //                              code).
    //   • `documents`            — mismatched LT-14 label. Deleted;
    //                              the placeholder text never
    //                              matched what the tab does.
    //
    // Convention (locked, third recurrence): a phaseInfo entry MUST
    // NOT exist for a `tab` that has a matching `if (tab === "…")`
    // branch above. Any commit that adds a real tab component MUST
    // remove the corresponding phaseInfo entry in the same diff. The
    // linter-friendly way to enforce this is a jest test that
    // asserts no key appears in both this map and the tab switch —
    // added in the same batch (see `tests/test_lt17_no_stub_outlives_feature.js`
    // — pending; currently policed by review).
    questionnaire:     { phase: "LT-6",   title: "Questionnaire",             hint: "Deliberately parked — the questionnaire will drive line-item selection from the rate library, which needs more historical depth first." },
  };
  const p = phaseInfo[tab] || { phase: "—", title: tab, hint: "" };
  return (
    <div className="mt-6">
      <Card>
        <EmptyState
          icon={p.icon || Construction}
          title={`${p.title} — coming in ${p.phase}`}
          hint={p.hint}
        />
      </Card>
    </div>
  );
}

export default function TenderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  // LT-39 — deep-link aliases (iteration_50 follow-up): ?tab=subby_quotes / subby-quotes → subby.
  const TAB_ALIASES = { subby_quotes: "subby", "subby-quotes": "subby" };
  const rawTab = search.get("tab") || "summary";
  const tab = TAB_ALIASES[rawTab] || rawTab;
  const setTab = (t) => setSearch({ tab: t }, { replace: true });

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  const { showPicker: showBrandChip } = useEnabledBrands();

  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  // LT-12 — Export PDF modal open state.
  const [exportPdfOpen, setExportPdfOpen] = useState(false);
  // LT-13 — Supplier Enquiry drawer (opened from inside the Export modal).
  const [supplierDrawerOpen, setSupplierDrawerOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/tenders/${id}`)
      .then((r) => setT(r.data))
      .catch(() => toast.error("Tender not found"))
      .finally(() => setLoading(false));
  }, [id]);

  // LT-Archive-Trash · action handlers
  const doArchive = async () => {
    try {
      await api.post(`/tenders/${t.id}/archive`);
      const { data } = await api.get(`/tenders/${t.id}`);
      setT(data);
      toast.success("Tender archived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };
  const doUnarchive = async () => {
    try {
      await api.post(`/tenders/${t.id}/unarchive`);
      const { data } = await api.get(`/tenders/${t.id}`);
      setT(data);
      toast.success("Tender unarchived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unarchive failed");
    }
  };
  const doTrash = async () => {
    try {
      await api.delete(`/tenders/${t.id}`);
      toast.success("Tender sent to Trash — restorable from the Trash tab");
      navigate("/tenders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const doRestore = async () => {
    try {
      await api.post(`/tenders/${t.id}/restore`);
      const { data } = await api.get(`/tenders/${t.id}`);
      setT(data);
      toast.success("Tender restored from Trash");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };
  const doPermanentDelete = async () => {
    try {
      const { data } = await api.delete(`/tenders/${t.id}/permanent`);
      toast.success(`Tender permanently deleted (${Object.values(data.cascade_counts || {}).reduce((a, b) => a + b, 0)} child rows removed)`);
      navigate("/tenders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Permanent delete failed");
    }
  };

  if (loading) {
    return <div className="app-container py-8 text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;
  }
  if (!t) {
    return (
      <div className="app-container py-8">
        <Card>
          <EmptyState
            icon={Construction}
            title="Tender not found"
            hint="It may have been archived or the URL is incorrect."
            action={<PrimaryButton onClick={() => navigate("/tenders")}>Back to tenders</PrimaryButton>}
          />
        </Card>
      </div>
    );
  }

  const addr = [t.site_address_line_1, t.site_address_line_2, t.site_city].filter(Boolean).join(", ");

  return (
    <div className="app-container pt-7" style={{ paddingBottom: 64 }}>
      <div className="flex items-center gap-1.5 text-ink-3 mb-4" style={{ fontSize: 12 }}>
        <button onClick={() => navigate("/tenders")} className="hover:text-ink-2">Tenders</button>
        <ChevronRight size={12} />
        <span style={{ textTransform: "capitalize" }}>{t.status}</span>
        <ChevronRight size={12} />
        <span className="text-ink-2">{t.ref} — {t.client_name}</span>
      </div>

      <div className="flex items-start justify-between gap-8 mb-5">
        <div className="flex-1">
          <h1 className="font-display text-ink" style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.012em", lineHeight: "1.15" }}>
            {t.client_name}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className="font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{t.ref}</span>
            <span className="text-ink-4">•</span>
            <span className="text-ink-2" style={{ fontSize: 12.5 }}>
              {addr || "—"}{t.site_postcode && (<span className="font-mono tnum text-ink-3 ml-1">{t.site_postcode}</span>)}
            </span>
            <span className="text-ink-4">•</span>
            <span className="text-ink-2" style={{ fontSize: 12.5 }}>{jobTypeLabelFor(t.job_type) || "No job type"}</span>
            {showBrandChip && (
              <>
                <span className="text-ink-4">•</span>
                <span className="text-ink-2" style={{ fontSize: 12.5 }}>
                  <span
                    className="inline-flex items-center justify-center rounded font-mono mr-1"
                    style={{
                      background: t.brand === "lux_construction" ? "#E8EFE9" : "#FEF3E2",
                      color: t.brand === "lux_construction" ? "#2F4F3E" : "#B45309",
                      border: `1px solid ${t.brand === "lux_construction" ? "#D6E2D8" : "#F2DFB8"}`,
                      padding: "2px 6px", fontSize: 9.5, fontWeight: 600
                    }}
                  >{t.brand === "lux_construction" ? "CON" : "DEV"}</span>
                  {BRAND_LABEL[t.brand]}
                </span>
              </>
            )}
            <span className="text-ink-4">•</span>
            <span className="text-ink-3" style={{ fontSize: 12.5 }}>
              Created <span className="font-mono tnum text-ink-2">{ukDate(t.created_at)}</span>
            </span>
            <StatusPill status={t.status} />
            {/* LT-Estimate-UX-01 Section 8 dependency — won / lost marking.
                Disabled while the tender is a draft (no meaningful outcome
                yet) or in the trash / archive (state resolution required
                first). Header-variant chip. */}
            <OutcomeChip
              tender={t}
              onOutcomeChanged={(res) => setT((prev) => ({
                ...prev,
                status: res.status,
                outcome_recorded_at: res.outcome_recorded_at,
                outcome_note: res.outcome_note,
              }))}
              disabled={t.status === "draft" || t.status === "archived" || !!t.deleted_at}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <HistoryButton tenderId={t.id} />
          {/* LT-Archive-Trash · action buttons. In-Trash tenders show Restore
              + Permanent-delete only (admin view). Live tenders show
              Archive/Unarchive + Delete. */}
          {t.deleted_at ? (
            <>
              <QuietButton icon={RotateCcw} onClick={doRestore} data-testid="tender-restore-btn">
                Restore
              </QuietButton>
              {isAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-note-red hover:bg-note-red/5 border border-note-red/30"
                      style={{ fontSize: 12 }}
                      data-testid="tender-permanent-delete-btn"
                    >
                      <Trash2 size={12} /> Delete permanently
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Permanently delete this tender?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This cannot be undone. The tender, its line items, and its
                        version history will be removed. The audit trail is retained.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="tender-permanent-delete-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={doPermanentDelete}
                        className="bg-note-red text-white hover:bg-note-red/90"
                        data-testid="tender-permanent-delete-confirm"
                      >
                        Delete permanently
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </>
          ) : (
            <>
              {t.status === "archived" ? (
                <QuietButton icon={ArchiveRestore} onClick={doUnarchive} data-testid="tender-unarchive-btn">
                  Unarchive
                </QuietButton>
              ) : (
                <QuietButton icon={ArchiveIcon} onClick={doArchive} data-testid="tender-archive-btn">
                  Archive
                </QuietButton>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-note-red hover:bg-note-red/5"
                    style={{ fontSize: 12 }}
                    data-testid="tender-delete-btn"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send this tender to Trash?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The tender will be hidden from every view and only visible to
                      administrators in the Trash tab. You can restore it later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="tender-delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={doTrash}
                      className="bg-note-red text-white hover:bg-note-red/90"
                      data-testid="tender-delete-confirm"
                    >
                      Send to Trash
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {/* LT-16 Part 4c — the top Export button now says LUX Copy /
              CSV explicitly. Client-facing outputs come only from
              Adjudication → Create Client Estimate. The old
              stand-alone "Issue Revision" button was retired: it
              duplicated the Create-Client-Estimate blocker-gate flow
              and split "commit a client version" across two buttons.
              A tender's next version is now always Create Client
              Estimate, from the Adjudication tab. */}
          <PrimaryButton
            icon={FileDown}
            onClick={() => setExportPdfOpen(true)}
            data-testid="tender-export-pdf-btn"
          >
            Export (LUX Copy · CSV)
          </PrimaryButton>
        </div>
      </div>

      {/* LT-Archive-Trash · in-Trash banner (admin only sees this) */}
      {t.deleted_at && (
        <div
          className="mb-4 px-4 py-3 rounded-md border"
          style={{ background: "#FFF7F7", borderColor: "#F4C4C4", color: "#7C1D1D", fontSize: 12.5 }}
          data-testid="tender-trash-banner"
        >
          <strong>This tender is in Trash.</strong> Sent to Trash on{" "}
          <span className="font-mono tnum">{ukDate(t.deleted_at)}</span>. Restore or permanently delete using the buttons above.
        </div>
      )}

      <AuditDrawer tenderId={t.id} tenderRef={t.ref} />
      <ExportPdfDialog
        open={exportPdfOpen}
        onOpenChange={setExportPdfOpen}
        tenderId={t.id}
        tenderRef={t.ref}
        onOpenSupplierEnquiry={() => setSupplierDrawerOpen(true)}
      />
      <SupplierEnquiryDrawer
        open={supplierDrawerOpen}
        onOpenChange={setSupplierDrawerOpen}
        tenderId={t.id}
        tenderRef={t.ref}
      />

      {/* LT-16 Part 3a-ii — Prepared/Issued state banner, above the
          tab bar so it survives every tab switch. Working state
          renders nothing. */}
      <LifecycleStateBanner tender={t} onTenderUpdate={setT} />

      <TabBar active={tab} onChange={setTab} />

      <div className="flex items-center justify-between gap-6 py-5 border-b border-line-soft">
        <div className="flex items-center gap-9">
          <StatChip label="Phases"      value={(t.phases || []).length || "—"} />
          <StatChip label="Line items"  value={(t.phases || []).reduce((n, p) => n + (p.line_count || 0), 0) || "—"} mono />
          <StatChip label="Hours total" value={
            (() => {
              const h = (t.phases || []).reduce((n, p) => n + (p.hours_total || 0), 0);
              return h ? `${h.toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h` : "—";
            })()
          } mono />
          <StatChip label="Cost basis"  value={gbp(t.cost_basis || 0)} mono />
          <StatChip label="Client total" value={gbp(t.client_total || t.estimated_value || 0)} mono />
        </div>
        <div className="flex items-center gap-2">
          {(t.phases || []).length === 0 && tab === "estimate" && (
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md"
                  style={{ background: "#E5EEF7", color: "#1F4E83", fontSize: 11.5, fontWeight: 500, border: "1px solid #CDDDED" }}>
              <Sparkles size={11} /> Apply a template to start, or add a phase manually
            </span>
          )}
        </div>
      </div>

      <TabContent tab={tab} tender={t} onTenderUpdate={setT} />

      <div className="mt-6 px-4 py-3 rounded-md bg-surface-2/60 border border-line text-ink-3" style={{ fontSize: 11.5 }}>
        <span className="font-medium text-ink-2">OneDrive folder (reserved):</span>{" "}
        <span className="font-mono tnum">{t.onedrive_folder_path || "—"}</span>
        <span className="ml-2 inline-flex items-center gap-1" style={{ color: "#B45309" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#D97706" }} /> stub
        </span>
      </div>
    </div>
  );
}
